import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  browserLocalPersistence,
  browserSessionPersistence,
  createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  GoogleAuthProvider,
  OAuthProvider,
  sendEmailVerification,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
} from "firebase/auth";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";

import { auth, db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { applyWaitlistApprovalToNewUser } from "../services/adminApprovalService";
import { initializeUserPerks } from "../services/userPerksService";
import { reserveUsername } from "../services/usernameService";
import { NICHE_OPTIONS } from "./niche";
import { peekSignupReferralCode, persistSignupReferralCode, writeReferralIntent } from "../services/referralIntentService";
import { markA2hsPendingAfterSignup } from "../utils/a2hs";
import { devLogin, DEV_TEST_EMAIL, isLocalDev } from "../utils/devAuth";
import ModalHeader from "./ModalHeader";
import "./LoginOverlay.css";

const googleProvider = new GoogleAuthProvider();
const appleProvider = new OAuthProvider("apple.com");

function IconEye({ visible }) {
  if (visible) {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function IconGoogle() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
    </svg>
  );
}

function IconApple() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="var(--text, #0E0C0A)" aria-hidden="true">
      <path d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.164 6.839 9.49.5.09.682-.218.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.463-1.11-1.463-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.337 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.16 22 16.416 22 12c0-5.523-4.477-10-10-10z" />
    </svg>
  );
}

function passwordStrength(val) {
  let score = 0;
  if (val.length >= 8) score++;
  if (/[A-Z]/.test(val)) score++;
  if (/[0-9]/.test(val)) score++;
  if (/[^A-Za-z0-9]/.test(val)) score++;
  const labels = ["", "Too short", "Could be stronger", "Almost there", "Strong password"];
  const cls = ["", "weak", "ok", "ok", "strong"];
  return { score, label: val.length === 0 ? "Enter a password" : labels[score] || "", segClass: cls[score] || "" };
}

function isValidCanadianPostal(val) {
  const clean = val.replace(/\s/g, "").toUpperCase();
  return /^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(clean);
}

function usernameFromSignup(name, email) {
  const fromName = name.trim().toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 20);
  const fromEmail = email.split("@")[0].toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 20);
  return fromName || fromEmail || "user";
}

const CREATOR_NICHES = NICHE_OPTIONS;

/**
 * @param {{ initialMode?: 'signin' | 'signup' | 'forgot', onClose: () => void }} props
 */
export default function LoginOverlay({ initialMode = "signin", onClose }) {
  const navigate = useNavigate();
  const { refreshUserProfile, refreshUserPerks } = useAuth();
  const showDevLogin = isLocalDev();
  const refCode = peekSignupReferralCode();

  const [tab, setTab] = useState(initialMode === "signup" ? "signup" : "signin");
  const [view, setView] = useState(initialMode === "forgot" ? "forgot" : "form");

  const [signInEmail, setSignInEmail] = useState("");
  const [signInPassword, setSignInPassword] = useState("");
  const [showSignInPw, setShowSignInPw] = useState(false);

  const [displayName, setDisplayName] = useState("");
  const [signUpEmail, setSignUpEmail] = useState("");
  const [signUpPassword, setSignUpPassword] = useState("");
  const [showSignUpPw, setShowSignUpPw] = useState(false);
  const [accountType, setAccountType] = useState("fan");
  const [selectedNiche, setSelectedNiche] = useState("");
  const [postalCode, setPostalCode] = useState("");

  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [staySignedIn, setStaySignedIn] = useState(false);

  const [loading, setLoading] = useState(false);
  const [devLoading, setDevLoading] = useState(false);
  const [error, setError] = useState("");
  const [shakeBtn, setShakeBtn] = useState(false);

  const strength = useMemo(() => passwordStrength(signUpPassword), [signUpPassword]);

  useEffect(() => {
    persistSignupReferralCode(refCode);
  }, [refCode]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  useEffect(() => {
    setTab(initialMode === "signup" ? "signup" : "signin");
    setView(initialMode === "forgot" ? "forgot" : "form");
    setError("");
    setForgotSent(false);
  }, [initialMode]);

  const switchTab = (next) => {
    setTab(next);
    setView("form");
    setError("");
    setForgotSent(false);
    if (next === "signin") {
      setSelectedNiche("");
      setAccountType("fan");
    }
  };

  const showForgot = () => {
    setView("forgot");
    setError("");
    setForgotSent(false);
    setForgotEmail(signInEmail);
  };

  const triggerShake = () => {
    setShakeBtn(true);
    setTimeout(() => setShakeBtn(false), 500);
  };

  const finishAuth = () => {
    navigate("/", { replace: true });
  };

  const finishSignup = () => {
    markA2hsPendingAfterSignup();
    finishAuth();
  };

  const applyAuthPersistence = async () => {
    await setPersistence(
      auth,
      staySignedIn ? browserLocalPersistence : browserSessionPersistence
    );
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!signInEmail.trim() || !signInPassword.trim()) {
      setError("Please fill in all fields.");
      triggerShake();
      return;
    }
    try {
      setLoading(true);
      setError("");
      await applyAuthPersistence();
      const credential = await signInWithEmailAndPassword(auth, signInEmail.trim(), signInPassword);
      await refreshUserProfile(credential.user.uid);
      finishAuth();
    } catch (err) {
      console.error("Login error:", err);
      setError("Incorrect email or password. Try again.");
      triggerShake();
    } finally {
      setLoading(false);
    }
  };

  const handleSocialSignIn = async (provider, label) => {
    try {
      setLoading(true);
      setError("");
      await applyAuthPersistence();
      const credential = await signInWithPopup(auth, provider);
      const extra = getAdditionalUserInfo(credential);
      if (extra?.isNewUser && refCode) {
        try {
          await writeReferralIntent({
            uid: credential.user.uid,
            email: credential.user.email || "",
            code: refCode,
          });
        } catch (refErr) {
          console.warn("Referral intent error:", refErr);
        }
      }
      await refreshUserProfile(credential.user.uid);
      await refreshUserPerks(credential.user.uid);
      if (extra?.isNewUser) finishSignup();
      else finishAuth();
    } catch (err) {
      console.error(`${label} sign-in error:`, err);
      if (err?.code === "auth/popup-closed-by-user" || err?.code === "auth/cancelled-popup-request") {
        setError("");
      } else if (err?.code === "auth/operation-not-allowed" || err?.code === "auth/unauthorized-domain") {
        setError(`${label} sign-in isn’t enabled for this project yet.`);
        triggerShake();
      } else {
        setError(`Could not sign in with ${label}. Try again.`);
        triggerShake();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!displayName.trim() || !signUpEmail.trim() || !signUpPassword.trim()) {
      setError("Please fill in all fields.");
      triggerShake();
      return;
    }
    if (signUpPassword.length < 6) {
      setError("Password should be at least 6 characters.");
      triggerShake();
      return;
    }
    if (accountType === "creator" && !selectedNiche) {
      setError("Please select a niche for your creator account.");
      triggerShake();
      return;
    }
    if (accountType === "creator" && postalCode.trim() && !isValidCanadianPostal(postalCode)) {
      setError("Enter a valid Canadian postal code (e.g. M5V 2T6).");
      triggerShake();
      return;
    }

    const wantsCreator = accountType === "creator";
    const cleanUsername = usernameFromSignup(displayName, signUpEmail);
    const signupEmail = signUpEmail.trim();

    try {
      setLoading(true);
      setError("");
      await applyAuthPersistence();
      if (refCode) {
        try {
          await writeReferralIntent({ email: signupEmail, code: refCode });
        } catch (refErr) {
          console.warn("Referral intent (pre-auth) error:", refErr);
        }
      }
      const result = await createUserWithEmailAndPassword(auth, signupEmail, signUpPassword);
      const finalUsername = await reserveUsername({ username: cleanUsername, uid: result.user.uid });

      const profile = {
        uid: result.user.uid,
        email: result.user.email || signupEmail.toLowerCase(),
        displayName: displayName.trim(),
        username: finalUsername,
        handle: finalUsername,
        bio: "",
        avatar: "",
        bannerImage: "",
        profileBackgroundType: "color",
        profileBackgroundValue: "#070b14",
        theme: "warmCream",
        isCreator: wantsCreator,
        role: wantsCreator ? "creator" : "fan",
        approved: false,
        creatorOnboardingComplete: false,
        followerCount: 0,
        supporterCount: 0,
        totalPosts: 0,
        profilePrivate: false,
        allowMessages: true,
        showActivity: true,
        creatorEmails: true,
        matureContent: false,
        ...(wantsCreator && selectedNiche ? { niche: [selectedNiche] } : {}),
        ...(wantsCreator && postalCode.trim()
          ? { postalCode: postalCode.replace(/\s/g, "").toUpperCase() }
          : {}),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(doc(db, "users", result.user.uid), profile, { merge: true });
      await applyWaitlistApprovalToNewUser(result.user.uid, profile.email);
      await initializeUserPerks(result.user.uid);

      try {
        await sendEmailVerification(result.user);
      } catch (verificationError) {
        console.warn("Verification email warning:", verificationError);
      }

      if (refCode) {
        try {
          await writeReferralIntent({
            uid: result.user.uid,
            email: result.user.email || signupEmail,
            code: refCode,
          });
        } catch (refErr) {
          console.warn("Referral intent error:", refErr);
        }
      }

      finishSignup();
    } catch (err) {
      console.error("Signup error:", err);
      setError(err.message || "Could not create account.");
      triggerShake();
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) {
      setError("Please enter your email address.");
      triggerShake();
      return;
    }
    try {
      setLoading(true);
      setError("");
      await sendPasswordResetEmail(auth, forgotEmail.trim());
      setForgotSent(true);
    } catch (err) {
      console.error("Password reset error:", err);
      setError("Could not send reset link. Check the email and try again.");
      triggerShake();
    } finally {
      setLoading(false);
    }
  };

  const handleDevLogin = async () => {
    try {
      setDevLoading(true);
      setError("");
      await applyAuthPersistence();
      const { user } = await devLogin();
      await refreshUserProfile(user.uid);
      await refreshUserPerks(user.uid);
      finishAuth();
    } catch (err) {
      console.error("Dev login error:", err);
      setError(
        err.code === "auth/dev-password-mismatch" || err.code === "auth/invalid-credential"
          ? err.message ||
              "Dev account exists with a different password. Set REACT_APP_DEV_ACCOUNT_PASSWORD in .env.local or reset the Firebase Auth user."
          : err.message || "Dev login failed. Check the browser console for details."
      );
    } finally {
      setDevLoading(false);
    }
  };

  const headingContent =
    view === "forgot" ? (
      <>Reset your <em>password.</em></>
    ) : tab === "signin" ? (
      <>Welcome <em>back.</em></>
    ) : (
      <>Join <em>100ViewsOnly.</em></>
    );

  const headingSub =
    view === "forgot"
      ? "We'll send you a link right away."
      : tab === "signin"
        ? "Sign in to your account to continue."
        : "Canada's first creator platform. It's free to start.";

  const postalHint = (() => {
    if (!postalCode.trim()) {
      return { text: "Creators must be based in Canada. Used for verification only.", className: "lo-field-hint" };
    }
    if (isValidCanadianPostal(postalCode)) {
      return { text: "✓ Valid Canadian postal code", className: "lo-field-hint good" };
    }
    return { text: "Enter a valid Canadian postal code (e.g. M5V 2T6)", className: "lo-field-hint error" };
  })();

  return (
    <div className="lo-overlay-wrap" onClick={onClose} role="presentation">
      <div
        className="lo-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="lo-modal-heading"
      >
        <ModalHeader
          headline={headingContent}
          subtext={headingSub}
          onClose={onClose}
          headingId="lo-modal-heading"
        />

        <div className="lo-modal-body">
          {view === "form" && (
            <div className="lo-auth-tabs">
              <button
                type="button"
                className={`lo-at${tab === "signin" ? " active" : ""}`}
                onClick={() => switchTab("signin")}
              >
                Sign In
              </button>
              <button
                type="button"
                className={`lo-at${tab === "signup" ? " active" : ""}`}
                onClick={() => switchTab("signup")}
              >
                Create Account
              </button>
            </div>
          )}

          {view === "form" && tab === "signin" && (
            <form className="lo-auth-form" onSubmit={handleSignIn}>
              <div className="lo-field">
                <label className="lo-field-label" htmlFor="lo-si-email">
                  Email Address
                </label>
                <input
                  id="lo-si-email"
                  className={`lo-field-input${error ? " error" : ""}`}
                  type="email"
                  placeholder="you@example.com"
                  autoComplete="email"
                  value={signInEmail}
                  onChange={(e) => setSignInEmail(e.target.value)}
                />
              </div>

              <div className="lo-field">
                <div className="lo-forgot-row">
                  <label className="lo-field-label" htmlFor="lo-si-password">
                    Password
                  </label>
                  <button type="button" className="lo-forgot-link" onClick={showForgot}>
                    Forgot password?
                  </button>
                </div>
                <div className="lo-field-input-wrap">
                  <input
                    id="lo-si-password"
                    className={`lo-field-input${error ? " error" : ""}`}
                    type={showSignInPw ? "text" : "password"}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    value={signInPassword}
                    onChange={(e) => setSignInPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className={`lo-field-input-icon${showSignInPw ? " visible" : ""}`}
                    onClick={() => setShowSignInPw((v) => !v)}
                    aria-label={showSignInPw ? "Hide password" : "Show password"}
                  >
                    <IconEye visible={showSignInPw} />
                  </button>
                </div>
                {error && <span className="lo-field-hint error">{error}</span>}
              </div>

              <label className="lo-stay-signed">
                <input
                  type="checkbox"
                  checked={staySignedIn}
                  onChange={(e) => setStaySignedIn(e.target.checked)}
                />
                <span className="lo-stay-signed-box" aria-hidden="true" />
                <span className="lo-stay-signed-label">Stay signed in</span>
              </label>

              <button
                type="submit"
                className={`lo-btn-main${shakeBtn ? " shake" : ""}`}
                disabled={loading || devLoading}
              >
                {loading ? "Signing in…" : "Sign In"}
              </button>

              <div className="lo-auth-divider">
                <span>or continue with</span>
              </div>

              <div className="lo-social-auth">
                <button
                  type="button"
                  className="lo-social-btn"
                  disabled={loading || devLoading}
                  onClick={() => handleSocialSignIn(googleProvider, "Google")}
                >
                  <IconGoogle />
                  Continue with Google
                </button>
                <button
                  type="button"
                  className="lo-social-btn"
                  disabled={loading || devLoading}
                  onClick={() => handleSocialSignIn(appleProvider, "Apple")}
                >
                  <IconApple />
                  Continue with Apple
                </button>
              </div>

              <div className="lo-auth-terms">
                By signing in you agree to our <Link to="/terms" onClick={onClose}>Terms of Service</Link> and{" "}
                <Link to="/privacy" onClick={onClose}>Privacy Policy</Link>
              </div>

              <p className="lo-auth-switch">
                Don&apos;t have an account?{" "}
                <button type="button" onClick={() => switchTab("signup")}>
                  Join Free
                </button>
              </p>

              {showDevLogin && (
                <div className="lo-dev-login">
                  <p className="lo-dev-label">Local development only</p>
                  <button
                    type="button"
                    className="lo-dev-btn"
                    disabled={loading || devLoading}
                    onClick={handleDevLogin}
                  >
                    {devLoading ? "Signing in…" : `Dev login · ${DEV_TEST_EMAIL}`}
                  </button>
                </div>
              )}
            </form>
          )}

          {view === "form" && tab === "signup" && (
            <form className="lo-auth-form" onSubmit={handleSignUp}>
              <div className="lo-account-type">
                <button
                  type="button"
                  className={`lo-actype${accountType === "fan" ? " selected" : ""}`}
                  onClick={() => {
                    setAccountType("fan");
                    setSelectedNiche("");
                    setError("");
                  }}
                >
                  <div className="lo-actype-icon">👁</div>
                  <div className="lo-actype-name">Fan</div>
                  <div className="lo-actype-desc">Discover and unlock drops from Canadian creators</div>
                </button>
                <button
                  type="button"
                  className={`lo-actype${accountType === "creator" ? " selected" : ""}`}
                  onClick={() => {
                    setAccountType("creator");
                    setError("");
                  }}
                >
                  <div className="lo-actype-icon">✦</div>
                  <div className="lo-actype-name">Creator</div>
                  <div className="lo-actype-desc">Post drops and build your subscriber base</div>
                </button>
              </div>

              {accountType === "creator" && (
                <div className="lo-field">
                  <label className="lo-field-label" htmlFor="lo-su-niche">
                    Your niche
                  </label>
                  <select
                    id="lo-su-niche"
                    className={`lo-field-input lo-field-select${error === "Please select a niche for your creator account." ? " error" : ""}`}
                    value={selectedNiche}
                    onChange={(e) => {
                      setSelectedNiche(e.target.value);
                      if (error === "Please select a niche for your creator account.") {
                        setError("");
                      }
                    }}
                  >
                    <option value="">Select your niche</option>
                    {CREATOR_NICHES.map((n) => (
                      <option key={n.key} value={n.key}>
                        {n.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="lo-field">
                <label className="lo-field-label" htmlFor="lo-su-name">
                  Full Name
                </label>
                <input
                  id="lo-su-name"
                  className="lo-field-input"
                  type="text"
                  placeholder="Your name"
                  autoComplete="name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                />
              </div>

              <div className="lo-field">
                <label className="lo-field-label" htmlFor="lo-su-email">
                  Email Address
                </label>
                <input
                  id="lo-su-email"
                  className="lo-field-input"
                  type="email"
                  placeholder="you@example.com"
                  autoComplete="email"
                  value={signUpEmail}
                  onChange={(e) => setSignUpEmail(e.target.value)}
                />
              </div>

              <div className="lo-field">
                <label className="lo-field-label" htmlFor="lo-su-password">
                  Password
                </label>
                <div className="lo-field-input-wrap">
                  <input
                    id="lo-su-password"
                    className="lo-field-input"
                    type={showSignUpPw ? "text" : "password"}
                    placeholder="Min. 8 characters"
                    autoComplete="new-password"
                    value={signUpPassword}
                    onChange={(e) => setSignUpPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className={`lo-field-input-icon${showSignUpPw ? " visible" : ""}`}
                    onClick={() => setShowSignUpPw((v) => !v)}
                    aria-label={showSignUpPw ? "Hide password" : "Show password"}
                  >
                    <IconEye visible={showSignUpPw} />
                  </button>
                </div>
                <div className="lo-pw-strength">
                  <div className="lo-pws-bar">
                    {[1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className={`lo-pws-seg${i <= strength.score && strength.segClass ? ` ${strength.segClass}` : ""}`}
                      />
                    ))}
                  </div>
                  <span
                    className="lo-pws-label"
                    style={{
                      color:
                        strength.score === 4
                          ? "var(--green, #1a8c52)"
                          : strength.score >= 2
                            ? "var(--gold, #c4790a)"
                            : strength.score === 1
                              ? "var(--red, #d03050)"
                              : undefined,
                    }}
                  >
                    {strength.label}
                  </span>
                </div>
              </div>

              {accountType === "creator" && (
                <div className="lo-field">
                  <label className="lo-field-label" htmlFor="lo-su-postal">
                    Canadian Postal Code
                  </label>
                  <input
                    id="lo-su-postal"
                    className={`lo-field-input${postalCode.trim() && !isValidCanadianPostal(postalCode) ? " error" : ""}`}
                    type="text"
                    placeholder="M5V 2T6"
                    maxLength={7}
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                  />
                  <span className={postalHint.className}>{postalHint.text}</span>
                </div>
              )}

              {error && <span className="lo-field-hint error">{error}</span>}

              <label className="lo-stay-signed">
                <input
                  type="checkbox"
                  checked={staySignedIn}
                  onChange={(e) => setStaySignedIn(e.target.checked)}
                />
                <span className="lo-stay-signed-box" aria-hidden="true" />
                <span className="lo-stay-signed-label">Stay signed in</span>
              </label>

              <button
                type="submit"
                className={`lo-btn-main${shakeBtn ? " shake" : ""}`}
                disabled={loading}
              >
                {loading ? "Creating account…" : "Create Account"}
              </button>

              <div className="lo-auth-divider">
                <span>or continue with</span>
              </div>

              <div className="lo-social-auth">
                <button
                  type="button"
                  className="lo-social-btn"
                  disabled={loading}
                  onClick={() => handleSocialSignIn(googleProvider, "Google")}
                >
                  <IconGoogle />
                  Continue with Google
                </button>
              </div>

              <div className="lo-auth-terms">
                By creating an account you agree to our{" "}
                <Link to="/terms" onClick={onClose}>Terms of Service</Link> and{" "}
                <Link to="/privacy" onClick={onClose}>Privacy Policy</Link>. We&apos;re CASL and PIPEDA compliant. 🍁
              </div>

              <p className="lo-auth-switch">
                Already have an account?{" "}
                <button type="button" onClick={() => switchTab("signin")}>
                  Sign In
                </button>
              </p>
            </form>
          )}

          {view === "forgot" && !forgotSent && (
            <form className="lo-auth-form" onSubmit={handleForgot}>
              <div>
                <div className="lo-forgot-intro-title">Reset your password</div>
                <div className="lo-forgot-intro-sub">
                  Enter your email and we&apos;ll send you a link to reset your password.
                </div>
              </div>
              <div className="lo-field">
                <label className="lo-field-label" htmlFor="lo-fp-email">
                  Email Address
                </label>
                <input
                  id="lo-fp-email"
                  className={`lo-field-input${error ? " error" : ""}`}
                  type="email"
                  placeholder="you@example.com"
                  autoComplete="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                />
                {error && <span className="lo-field-hint error">{error}</span>}
              </div>
              <button type="submit" className={`lo-btn-main${shakeBtn ? " shake" : ""}`} disabled={loading}>
                {loading ? "Sending…" : "Send Reset Link"}
              </button>
              <div style={{ textAlign: "center", marginTop: 4 }}>
                <button type="button" className="lo-back-link" onClick={() => { setView("form"); setTab("signin"); setError(""); }}>
                  ← Back to sign in
                </button>
              </div>
            </form>
          )}

          {view === "forgot" && forgotSent && (
            <div className="lo-forgot-sent">
              <div className="lo-forgot-intro-title">Check your inbox</div>
              <div className="lo-forgot-intro-sub" style={{ marginTop: 6 }}>
                We sent a reset link to <strong>{forgotEmail}</strong>. Check your spam folder if you don&apos;t see it.
              </div>
              <div style={{ marginTop: 16 }}>
                <button
                  type="button"
                  className="lo-back-link"
                  onClick={() => { setView("form"); setTab("signin"); setForgotSent(false); setError(""); }}
                >
                  ← Back to sign in
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
